import { supabase } from '@/integrations/supabase/client';

export interface DataValidationResult {
  isValid: boolean;
  issues: string[];
  userStats: any;
  actualCounts: {
    activities: number;
    habits: number;
    dailies: number;
    todos: number;
  };
}

/**
 * Validates user data consistency between stats and actual records
 */
export async function validateUserData(userId: string): Promise<DataValidationResult> {
  console.log("🔍 Validating user data consistency...");
  
  try {
    // Get user stats
    const { data: userStats, error: statsError } = await supabase
      .from('user_stats')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();

    if (statsError) throw statsError;

    // Count actual records
    const [activitiesRes, habitsRes, dailiesRes, todosRes] = await Promise.all([
      supabase.from('activities').select('id', { count: 'exact' }).eq('user_id', userId),
      supabase.from('habits').select('id', { count: 'exact' }).eq('user_id', userId),
      supabase.from('dailies').select('id', { count: 'exact' }).eq('user_id', userId),
      supabase.from('todos').select('id', { count: 'exact' }).eq('user_id', userId)
    ]);

    const actualCounts = {
      activities: activitiesRes.count || 0,
      habits: habitsRes.count || 0,
      dailies: dailiesRes.count || 0,
      todos: todosRes.count || 0
    };

    // Check for inconsistencies
    const issues: string[] = [];
    
    if (!userStats) {
      issues.push("No user stats found");
      return { isValid: false, issues, userStats: null, actualCounts };
    }

    // Check activities consistency
    if (userStats.activities_logged_today > 0 && actualCounts.activities === 0) {
      issues.push(`Stats show ${userStats.activities_logged_today} activities today but activities table is empty`);
    }

    // Check if activity counts make sense
    const totalActivityCount = Object.values(userStats.activity_counts || {}).reduce((sum: number, count: any) => sum + (count || 0), 0);
    if (totalActivityCount > 0 && actualCounts.activities === 0) {
      issues.push(`Activity counts show ${totalActivityCount} total activities but activities table is empty`);
    }

    // Check if user has points but no activities
    if (userStats.total_points > 0 && actualCounts.activities === 0) {
      issues.push(`User has ${userStats.total_points} total points but no activities`);
    }

    const isValid = issues.length === 0;
    
    console.log("🔍 Validation result:", { isValid, issues, actualCounts });
    
    return { isValid, issues, userStats, actualCounts };
    
  } catch (error) {
    console.error("❌ Error validating user data:", error);
    return {
      isValid: false,
      issues: [`Validation failed: ${error}`],
      userStats: null,
      actualCounts: { activities: 0, habits: 0, dailies: 0, todos: 0 }
    };
  }
}

/**
 * Resets user stats to match actual data counts
 */
export async function resetUserStatsToMatchActualData(userId: string): Promise<void> {
  console.log("🔄 Resetting user stats to match actual data...");
  
  try {
    const validation = await validateUserData(userId);
    
    if (!validation.userStats) {
      throw new Error("No user stats found to reset");
    }

    // Reset stats based on actual data
    const resetStats = {
      total_points: 0,
      current_week_points: 0,
      activities_logged_today: 0,
      activity_counts: {},
      experience: 0,
      activity_credits: 0,
      streak: 0,
      weekly_bonus_earned: 0,
      badges: []
    };

    const { error } = await supabase
      .from('user_stats')
      .update(resetStats)
      .eq('user_id', userId);

    if (error) throw error;

    console.log("✅ User stats reset successfully");
    
  } catch (error) {
    console.error("❌ Error resetting user stats:", error);
    throw error;
  }
}